/**
 * Form Submission Utilities
 *
 * Shared utilities for processing form data with files before submission.
 * Used by useMyRequestDetails and other form submission hooks.
 */

import { toast } from 'react-toastify';
import { t } from 'i18next';
import axiosInstance from '../../axiosInstance';

/**
 * Checks if a value is a base64 image string.
 * @param {*} value - Value to check
 * @returns {boolean}
 */
const isBase64Image = (value) => {
  if (typeof value !== 'string') return false;
  return (
    value.startsWith('data:image/') ||
    (value.length > 100 && /^[A-Za-z0-9+/=]+$/.test(value.replace(/\s/g, '')))
  );
};

/**
 * Checks if a value contains file data that needs multipart upload.
 * Note: Base64 images (signatures) are NOT treated as files - they stay as base64 in form_data.
 * @param {*} value - Value to check
 * @returns {boolean}
 */
const isFileValue = (value) => {
  if (value instanceof FileList && value.length > 0) return true;
  if (value instanceof File) return true;
  if (Array.isArray(value) && value.length > 0 && value[0] instanceof File)
    return true;
  // Signatures (base64 images) are stored directly in form_data, not uploaded as files
  // if (isBase64Image(value)) return true; // REMOVED - signatures stay as base64
  return false;
};

/**
 * Checks if form data contains files that need multipart upload.
 * Supports nested structure: { formId: { subformId: { fieldId: value } } }
 * @param {Object} allFormData - Nested form data object
 * @returns {boolean}
 */
export const hasFilesInFormData = (allFormData) => {
  // Handle nested structure: { formId: { subformId: { fieldId: value } } }
  for (const formId of Object.keys(allFormData)) {
    const formData = allFormData[formId];
    for (const subformId of Object.keys(formData)) {
      const subformData = formData[subformId];
      if (Object.values(subformData).some(isFileValue)) {
        return true;
      }
    }
  }
  return false;
};

/**
 * Appends a file value to FormData and returns the cleaned value for JSON.
 * @param {FormData} formData - FormData object to append to
 * @param {string} fieldId - Field identifier
 * @param {*} value - File value (File, FileList, Array<File>, or base64 string)
 * @returns {*} - Cleaned value (filename or array of filenames)
 */
const appendFileToFormData = (formData, fieldId, value) => {
  if (value instanceof FileList) {
    Array.from(value).forEach((file, index) => {
      formData.append(`file_${fieldId}_${index}`, file);
    });
    return Array.from(value).map((file) => file.name);
  }

  if (value instanceof File) {
    formData.append(`file_${fieldId}_0`, value);
    return value.name;
  }

  if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
    value.forEach((file, index) => {
      formData.append(`file_${fieldId}_${index}`, file);
    });
    return value.map((file) => file.name);
  }

  // Base64 images (signatures) are handled directly in form_data, not as file uploads
  // They are stored as-is in the database value field
  return value;
};

/**
 * Processes form data for submission, handling files and base64 images.
 * Supports nested structure: { formId: { subformId: { fieldId: value } } }
 * @param {Object} allFormData - Nested form data object
 * @param {FormData} formData - FormData object to append files to
 * @param {Object} options - Processing options
 * @param {boolean} options.trackNewFiles - Whether to track which fields have new files
 * @returns {{cleanedData: Object, fieldsWithNewFiles?: Set}}
 */
export const processFormDataForSubmission = (
  allFormData,
  formData,
  options = {}
) => {
  const { trackNewFiles = false } = options;
  const fieldsWithNewFiles = trackNewFiles ? new Set() : null;

  // Track processed (formId_subformId_fieldId) to prevent duplicate file uploads
  const processedFileFields = new Set();

  // Process nested structure: { formId: { subformId: { fieldId: value } } }
  const cleanedData = {};

  for (const formId of Object.keys(allFormData)) {
    cleanedData[formId] = {};
    const formSubforms = allFormData[formId];

    for (const subformId of Object.keys(formSubforms)) {
      const subformData = formSubforms[subformId] || {};
      const cleanedSubformData = {};

      Object.entries(subformData).forEach(([fieldId, value]) => {
        if (isFileValue(value)) {
          const fieldKey = `${formId}_${subformId}_${fieldId}`;

          // Skip if we already processed a file for this field
          if (processedFileFields.has(fieldKey)) {
            return;
          }
          processedFileFields.add(fieldKey);

          if (trackNewFiles) {
            fieldsWithNewFiles.add(`${subformId}_${fieldId}`);
          }
          cleanedSubformData[fieldId] = appendFileToFormData(
            formData,
            fieldId,
            value
          );
        } else {
          cleanedSubformData[fieldId] = value;
        }
      });

      cleanedData[formId][subformId] = cleanedSubformData;
    }
  }

  return trackNewFiles ? { cleanedData, fieldsWithNewFiles } : { cleanedData };
};

/**
 * Extracts and sorts subforms from a form or stage container.
 * @param {Object} formOrContainer - Form object with subforms, or container with Forms array
 * @param {Array} fallback - Fallback array if no subforms found
 * @returns {Array} Sorted subforms
 */
export const extractAndSortSubforms = (formOrContainer, fallback = []) => {
  // If formOrContainer has subforms directly, use them
  if (formOrContainer?.subforms?.length > 0) {
    return [...formOrContainer.subforms].sort(
      (a, b) => (a.forms_subforms?.order || 0) - (b.forms_subforms?.order || 0)
    );
  }

  // Otherwise try to get from Forms[0] (legacy container pattern)
  const form = formOrContainer?.Forms?.[0];
  if (form?.subforms?.length > 0) {
    return [...form.subforms].sort(
      (a, b) => (a.forms_subforms?.order || 0) - (b.forms_subforms?.order || 0)
    );
  }

  return fallback;
};

/**
 * Maps a workflow stage to an array of form objects for multi-form submission.
 * Each form gets its own entry with subforms extracted and sorted.
 * Forms are sorted by sort_order from the StageForms junction table.
 * @param {Object} stage - Stage object with Forms array
 * @returns {Array} Array of form objects ready for submission UI
 */
export const mapStageToForms = (stage) => {
  const forms = stage.Forms || [];

  if (forms.length === 0) {
    return [
      {
        id: stage.id,
        stageId: stage.id,
        name: stage.name,
        subforms: extractAndSortSubforms(stage, []),
        form: null,
        stage,
      },
    ];
  }

  // Sort forms by sort_order from junction table before mapping
  const sortedForms = [...forms].sort((a, b) => {
    return (a.StageForms?.sort_order || 0) - (b.StageForms?.sort_order || 0);
  });

  return sortedForms.map((form) => ({
    id: form.id,
    stageId: stage.id,
    name: form.name,
    subforms: extractAndSortSubforms(form, []),
    form,
    stage,
  }));
};

/**
 * Submits form data with optional file handling.
 * Consolidates submission logic for both new and existing requests.
 *
 * @param {Object} options - Submission options
 * @param {Array} options.allFormData - Form data array [{subformId: {fieldId: value}}]
 * @param {Object} options.payload - Additional payload fields (stage_id, request_progress_id, etc.)
 * @param {Array} options.fileData - Files from new file module with metadata [{id, formId, subformId, fieldId}]
 * @param {number} options.draftId - ID of draft to delete after successful submission
 * @param {Function} options.onSuccess - Success callback
 * @param {Function} options.onError - Error callback
 * @param {boolean} options.showToast - Whether to show toast notifications (default: true)
 * @returns {Promise<boolean>} - Returns true on success, false on failure
 */
export const submitFormData = async ({
  allFormData,
  payload = {},
  fileData = [], // New file module - includes metadata [{id, formId, subformId, fieldId}]
  draftId = null,
  onSuccess,
  onError,
  showToast = true,
  returnResponse = false,
}) => {
  const hasNewFiles = hasFilesInFormData(allFormData);
  const hasFileData = fileData.length > 0;
  const hasFiles = hasNewFiles || hasFileData;

  try {
    if (hasFiles) {
      const formData = new FormData();

      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          // Arrays/objects must be JSON-stringified for FormData,
          // otherwise .toString() turns [1,2] into "1,2" (invalid JSON).
          const serialized =
            Array.isArray(value) ||
            (typeof value === 'object' && value !== null)
              ? JSON.stringify(value)
              : value;
          formData.append(key, serialized);
        }
      });

      const { cleanedData } = processFormDataForSubmission(
        allFormData,
        formData
      );

      formData.append('value', JSON.stringify(cleanedData));

      // NOTE: existingFileIds no longer sent - attachments persist in EndUserFilledFormAttachment
      // NOTE: deletedFileIds no longer sent - deletions are now immediate via dedicated endpoints

      // Include new file module data with metadata (promotes STAGED -> AVAILABLE)
      // Backend uses this metadata to set field_id, subform_id, form_id in attachments
      if (hasFileData) {
        formData.append('file_data', JSON.stringify(fileData));
      }

      // Include draft ID to delete/transfer files after successful submission
      if (draftId) {
        formData.append('draft_id', draftId);
      }

      const response = await axiosInstance.post(
        '/api/endUserFilledForms',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      if (showToast) {
        toast.success(t('Form Saved Successfully!'), {
          position: 'top-center',
        });
      }
      onSuccess?.(response?.data);
      return returnResponse ? { success: true, data: response?.data } : true;
    } else {
      const response = await axiosInstance.post('/api/endUserFilledForms', {
        ...payload,
        value: JSON.stringify(allFormData),
        // NOTE: deletedFileIds no longer sent - deletions are immediate
        ...(hasFileData && { file_data: fileData }),
        ...(draftId && { draft_id: draftId }),
      });
      if (showToast) {
        toast.success(t('Form Saved Successfully!'), {
          position: 'top-center',
        });
      }
      onSuccess?.(response?.data);
      return returnResponse ? { success: true, data: response?.data } : true;
    }
  } catch (err) {
    console.error('Error saving form:', err);
    if (showToast) {
      // Safely access validation error details — the backend may return
      // { error: { details: [...] } } for ValidationError, or a plain
      // { message: '...' } for other errors.
      const details = err?.response?.data?.error?.details;
      if (details) {
        const messages = Array.isArray(details)
          ? details.map((d) => d.message || 'Validation error')
          : Object.values(details);
        toast.error(messages.join('\n'), { position: 'top-center' });
      } else {
        const message =
          err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          'Form submission failed';
        toast.error(message, { position: 'top-center' });
      }
    }
    onError?.(err);
    return returnResponse ? { success: false, error: err } : false;
  }
};
